// Pure pricing functions for the instant-estimate calculator. No DOM here, so they can be
// tested with `npm test`. Rates come from rates.js.

import { RATES } from "./rates.js";

// States the company delivers to: the lower 48 plus DC.
const LOWER_48 = new Set(("AL AZ AR CA CO CT DE DC FL GA ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM " +
  "NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY").split(" "));

export function inServiceArea(place) {
  return LOWER_48.has(place.state);
}

// Road miles between two places ({lat, lon}), from straight-line distance times a road factor.
export function roadMiles(a, b, rates = RATES) {
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  const straight = 2 * 3958.8 * Math.asin(Math.sqrt(h));
  return Math.max(1, Math.round(straight * rates.roadFactor));
}

function range(mid, rates) {
  const r = (n) => Math.round(n / rates.roundTo) * rates.roundTo;
  return { low: r(mid * (1 - rates.spread)), high: r(mid * (1 + rates.spread)), mid: Math.round(mid) };
}

// Freight. input: { miles, pallets, lbs, fullLoad, liftgate, residential, rush, vehicle }
// vehicle is the id the customer picked ("van", "box", "semi"); left out, the smallest that fits is used.
// A pick too small for the load is swapped for the smallest that fits, and `swappedFrom` names the pick.
export function freightEstimate(input, rates = RATES) {
  const f = rates.freight;
  const pallets = input.fullLoad ? Infinity : Math.max(0, input.pallets || 0);
  const lbs = Math.max(0, input.lbs || 0);
  const fits = (v) => pallets <= v.maxPallets && lbs <= v.maxLbs;
  const picked = f.vehicles.find((v) => v.id === input.vehicle);
  const vehicle = input.fullLoad
    ? f.vehicles[f.vehicles.length - 1]
    : picked && fits(picked) ? picked : f.vehicles.find(fits);
  if (!vehicle) return { tooBig: true };
  const swappedFrom = picked && picked !== vehicle && !input.fullLoad ? picked.name : null;

  // The first shortHaulMiles are billed at the short-haul rate, as a floor: a run just past the
  // threshold never costs less than one just under it.
  const shortRate = vehicle.perMile * f.shortHaulMultiplier;
  const byMiles = input.miles * vehicle.perMile;
  const shortFloor = Math.min(input.miles, f.shortHaulMiles) * shortRate;
  const linehaul = Math.max(vehicle.minimum, byMiles, shortFloor);
  const miles = input.miles.toLocaleString("en-US");
  const lines = [{
    label: linehaul === vehicle.minimum ? `${vehicle.name}, minimum charge`
      : linehaul === byMiles ? `${miles} mi at $${vehicle.perMile.toFixed(2)}/mi`
      : input.miles < f.shortHaulMiles ? `${miles} mi at $${shortRate.toFixed(2)}/mi (short haul)`
      : `${miles} mi, short-haul minimum`,
    amount: linehaul,
  }];
  if (input.liftgate) lines.push({ label: "Liftgate", amount: f.liftgate });
  if (input.residential) lines.push({ label: "Residential delivery", amount: f.residential });
  let subtotal = sum(lines);
  if (input.rush) {
    const extra = subtotal * f.rush;
    lines.push({ label: `Rush / team drivers (+${Math.round(f.rush * 100)}%)`, amount: extra });
    subtotal += extra;
  }
  return { vehicle, swappedFrom, lines, ...range(subtotal, rates) };
}

// Household move. input: { miles, home, packing, stairs, heavyItems }
export function movingEstimate(input, rates = RATES) {
  const m = rates.moving;
  const home = m.homes[input.home];
  if (!home) return null;
  const vehicleId = input.miles <= m.localMiles && home.vehicle === "semi" ? "box" : home.vehicle;
  const vehicle = rates.freight.vehicles.find((v) => v.id === vehicleId);
  const perHour = m.hourly[home.crew];
  const local = input.miles <= m.localMiles;
  const lines = [];

  if (local) {
    const driveHours = input.miles / m.driveMph;
    const hours = Math.max(m.minimumHours, halfHours(home.hours + driveHours));
    lines.push({ label: `${home.crew} movers and truck, about ${hours} hrs at $${perHour}/hr`, amount: hours * perHour });
  } else {
    lines.push({ label: `${home.crew} movers, load and unload, about ${home.hours} hrs`, amount: home.hours * perHour });
    const perMile = m.perMile[vehicleId];
    lines.push({ label: `${input.miles.toLocaleString("en-US")} mi at $${perMile.toFixed(2)}/mi`, amount: input.miles * perMile });
  }
  if (input.packing) lines.push({ label: "Full packing", amount: lines[0].amount * m.packing });
  if (input.stairs > 0) lines.push({ label: `Stairs, ${input.stairs} flight${input.stairs > 1 ? "s" : ""}`, amount: input.stairs * m.stairsPerFlight });
  if (input.heavyItems > 0) lines.push({ label: `Heavy items × ${input.heavyItems}`, amount: input.heavyItems * m.heavyItem });

  return { vehicle, local, crew: home.crew, lines, ...range(sum(lines), rates) };
}

// Junk removal. input: { load, mattresses, appliances, tires, sameDay }
export function junkEstimate(input, rates = RATES) {
  const j = rates.junk;
  const load = j.loads[input.load];
  if (!load) return null;
  const lines = [{ label: load.label, amount: load.price }];
  if (input.mattresses > 0) lines.push({ label: `Mattresses × ${input.mattresses}`, amount: input.mattresses * j.mattress });
  if (input.appliances > 0) lines.push({ label: `Fridges / AC units × ${input.appliances}`, amount: input.appliances * j.appliance });
  if (input.tires > 0) lines.push({ label: `Tires × ${input.tires}`, amount: input.tires * j.tire });
  if (input.sameDay) lines.push({ label: "Same-day pickup", amount: j.sameDay });
  const vehicle = rates.freight.vehicles.find((v) => v.id === (input.load === "min" ? "van" : "box"));
  return { vehicle, lines, ...range(sum(lines), rates) };
}

function sum(lines) {
  return lines.reduce((t, l) => t + l.amount, 0);
}
// crew time is billed in half-hour steps, rounded up
function halfHours(n) {
  return Math.ceil(n * 2) / 2;
}
